import { Helmet } from 'react-helmet-async';
import { useState, useEffect, useMemo } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { PDFViewer, Page, Text, View, Document, StyleSheet, Font, Svg } from '@react-pdf/renderer';
import axios from 'axios';
import './styles.css';
import {
  Card,
  Table,
  Stack,
  Paper,
  Avatar,
  Button,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';

import Iconify from '../components/iconify';
import Scrollbar from '../components/scrollbar';

import { UserListHead, UserListToolbar } from '../sections/@dashboard/user';

const SERVER_URL = process.env.REACT_APP_BACKEND_SERVER_URL;

// ----------------------------------------------------------------------
// Kolone tabele
// search: 'exact' tačan broj | 'text' sadrži tekst | 'numeric' broj ili izraz | null bez pretrage
// sortable: false - kolona se ne može sortirati

const TABLE_HEAD = [
  { id: 'deviceId', label: 'Broj uređaja', alignRight: false, search: 'exact' },
  { id: 'tip', label: 'Tip', alignRight: false, search: 'text' },
  { id: 'vlasnik', label: 'Vlasnik', alignRight: false, search: 'text' },
  { id: 'imepsa', label: 'Ime psa', alignRight: false, search: 'text' },
  { id: 'IMEI', label: 'IMEI', alignRight: false, search: 'text' },
  { id: 'tiptrackera', label: 'Tip trackera', alignRight: false, search: 'text' },
  { id: 'posudjeno', label: 'Posuđeno korisniku', alignRight: false, search: 'text' },
  { id: 'update', label: 'Update', alignRight: false, search: null, sortable: false },
  { id: 'delete', label: 'Delete', alignRight: false, search: null, sortable: false },
];

const getColumn = (id) => TABLE_HEAD.find((c) => c.id === id);

// ----------------------------------------------------------------------
// Sortiranje i pretraga

function compareValues(a, b) {
  // Brojevi numerički, tekst abecedno (uz ispravno Č, Ć, Š, Ž, Đ)
  const na = Number(a);
  const nb = Number(b);
  if (a !== '' && b !== '' && a !== null && b !== null && !Number.isNaN(na) && !Number.isNaN(nb)) {
    return na - nb;
  }
  return String(a ?? '').localeCompare(String(b ?? ''), 'bs', { sensitivity: 'base' });
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => compareValues(b[orderBy], a[orderBy])
    : (a, b) => compareValues(a[orderBy], b[orderBy]);
}

// Brojčana pretraga: '3', '=3', '>0', '>=5', '<2', '<=1'
function matchNumeric(value, query) {
  const q = query.replace(/\s+/g, '');
  if (/^(>=|<=|>|<|=)?$/.test(q)) return true; // tek započet izraz, npr. samo '>'

  const m = q.match(/^(>=|<=|>|<|=)?(-?\d+)$/);
  if (!m) return false;

  const op = m[1] || '=';
  const n = Number(m[2]);
  const v = Number(value);

  switch (op) {
    case '>':
      return v > n;
    case '<':
      return v < n;
    case '>=':
      return v >= n;
    case '<=':
      return v <= n;
    default:
      return v === n;
  }
}

function matchesSearch(row, field, query) {
  const column = getColumn(field);
  const mode = column ? column.search : null;
  const q = query.trim().toLowerCase();

  switch (mode) {
    case 'exact':
      return /^\d+$/.test(q) ? Number(row[field]) === Number(q) : false;
    case 'text':
      return String(row[field] ?? '').toLowerCase().includes(q);
    case 'numeric':
      return matchNumeric(row[field], q);
    default:
      return true;
  }
}

function applySortFilter(array, comparator, query, field) {
  const sorted = array
    .map((el, index) => [el, index])
    .sort((a, b) => comparator(a[0], b[0]) || a[1] - b[1])
    .map((el) => el[0]);

  if (!query || !query.trim()) return sorted;

  return sorted.filter((row) => matchesSearch(row, field, query));
}

function getSearchPlaceholder(field) {
  const column = getColumn(field);
  if (!column) return 'Traži...';

  switch (column.search) {
    case 'exact':
      return `Traži po: ${column.label} (tačan broj)`;
    case 'text':
      return `Traži po: ${column.label}`;
    case 'numeric':
      return `Traži po: ${column.label} (npr. 1, >0, <=2)`;
    default:
      return `Pretraga nije dostupna za: ${column.label}`;
  }
}

// ----------------------------------------------------------------------
// Pretvaranje zapisa iz /shared_devices u ravan red za tabelu
// (device.subscriber = vlasnik, subscriber = korisnik kome je uređaj posuđen)

const punoIme = (osoba) => (osoba ? `${osoba.ime ?? ''} ${osoba.prezime ?? ''}`.trim() : '');

function uRed(share) {
  const device = share.device || {};
  return {
    ...share,
    deviceId: share.deviceId ?? device.id,
    tip: device.tip ?? '',
    tiptrackera: device.tiptrackera ?? '',
    imepsa: device.ime ?? '',
    IMEI: device.IMEI ?? '',
    vlasnik: punoIme(device.subscriber),
    posudjeno: punoIme(share.subscriber),
  };
}

// ----------------------------------------------------------------------
// PDF font i stilovi

Font.register({
  family: 'Roboto',
  src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-light-webfont.ttf',
});

const REPORT_COLS = [
  { key: 'rb', label: 'R.br.', width: '5%' },
  { key: 'vlasnik', label: 'Vlasnik', width: '22%', left: true },
  { key: 'imepsa', label: 'Ime psa', width: '15%', left: true },
  { key: 'IMEI', label: 'IMEI', width: '18%', left: true },
  { key: 'tiptrackera', label: 'Tip trackera', width: '15%', left: true },
  { key: 'posudjeno', label: 'Posuđeno korisniku', width: '25%', left: true },
];

const styles = StyleSheet.create({
  body: {
    paddingTop: 40,
    paddingBottom: 65,
    paddingHorizontal: 30,
    fontFamily: 'Roboto',
    fontSize: 10,
  },
  svgTitle: {
    marginVertical: 5,
    paddingHorizontal: 20,
  },
  textmediumlarge: {
    fontSize: 20,
    fontFamily: 'Roboto',
  },
  summary: {
    fontSize: 10,
    marginBottom: 12,
    paddingHorizontal: 20,
  },
  table: {
    display: 'table',
    width: 'auto',
  },
  tableRow: {
    margin: 0,
    flexDirection: 'row',
  },
  tableHeader: {
    borderBottomWidth: 1,
    borderBottomColor: '#000000',
    borderBottomStyle: 'solid',
    backgroundColor: '#e8f4e8',
  },
  rowOdd: {
    backgroundColor: '#f5f5f5',
  },
  tableCellLeft: {
    marginTop: 4,
    marginBottom: 4,
    marginLeft: 3,
    fontSize: 9,
    textAlign: 'left',
  },
  tableCellCenter: {
    marginTop: 4,
    marginBottom: 4,
    fontSize: 9,
    textAlign: 'center',
  },
  officer: {
    paddingTop: 50,
    left: 250,
  },
  officertext: {
    margin: 0,
    fontSize: 12,
    textAlign: 'center',
    fontFamily: 'Roboto',
  },
  mp: {
    position: 'absolute',
    fontSize: 12,
    bottom: 60,
    left: 0,
    right: 0,
    textAlign: 'center',
    color: 'grey',
  },
  pageNumber: {
    position: 'absolute',
    fontSize: 12,
    bottom: 30,
    left: 0,
    right: 0,
    textAlign: 'center',
    color: 'grey',
  },
});

function SharedDevicesReport({ rows, datum }) {
  return (
    <Document>
      <Page size="A4" orientation="landscape" style={styles.body} wrap>
        <Text style={styles.summary}>{datum}</Text>

        <Svg style={styles.svgTitle} width="800" height="40" viewBox="0 0 800 40">
          <Text style={styles.textmediumlarge} x="240" y="30">
            Pregled dijeljenih uređaja
          </Text>
        </Svg>

        <Text style={styles.summary}>Dijeljenja u izvještaju: {rows.length}</Text>

        <View style={styles.table}>
          <View style={{ ...styles.tableRow, ...styles.tableHeader }}>
            {REPORT_COLS.map((c) => (
              <View key={c.key} style={{ width: c.width }}>
                <Text style={c.left ? styles.tableCellLeft : styles.tableCellCenter}>{c.label}</Text>
              </View>
            ))}
          </View>

          {rows.map((row, index) => {
            const values = { ...row, rb: `${index + 1}.` };

            return (
              <View
                key={row.id}
                wrap={false}
                style={index % 2 ? { ...styles.tableRow, ...styles.rowOdd } : styles.tableRow}
              >
                {REPORT_COLS.map((c) => (
                  <View key={c.key} style={{ width: c.width }}>
                    <Text style={c.left ? styles.tableCellLeft : styles.tableCellCenter}>
                      {String(values[c.key] ?? '')}
                    </Text>
                  </View>
                ))}
              </View>
            );
          })}
        </View>

        <View style={styles.officer}>
          <Text style={styles.officertext}>______________________________</Text>
          <Text style={styles.officertext}>( Knjigovođa )</Text>
        </View>

        <Text style={styles.mp}>M.P.</Text>

        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
          fixed
        />
      </Page>
    </Document>
  );
}

// ----------------------------------------------------------------------

export default function SharedDevices() {
  const { user: currentUser } = useSelector((state) => state.auth);

  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('vlasnik');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Dijalog za prekid dijeljenja
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const getAllShares = async () => {
    try {
      const response = await axios.get(`${SERVER_URL}/shared_devices`);
      setShares(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.log('Greška pri učitavanju dijeljenih uređaja', err);
    }
  };

  // Učitaj jednom, pri otvaranju stranice
  useEffect(() => {
    if (!currentUser) return;
    getAllShares();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows = useMemo(() => shares.map(uRed), [shares]);

  const filteredRows = useMemo(
    () => applySortFilter(rows, getComparator(order, orderBy), filterName, orderBy),
    [rows, order, orderBy, filterName]
  );

  // Svi hookovi su iznad ove provjere
  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  // --- Handleri ---

  const setData = (data) => {
    const { id, deviceId, subscriberId } = data;
    localStorage.setItem('id', id);
    localStorage.setItem('deviceId', deviceId);
    localStorage.setItem('subscriberId', subscriberId);
  };

  const handleClickDelete = (row) => {
    setDeleteError('');
    setDeleteRow(row);
  };

  const handleCloseDelete = () => {
    if (deleting) return;
    setDeleteRow(null);
  };

  const handleConfirmDelete = async () => {
    if (!deleteRow) return;
    setDeleting(true);
    try {
      await axios.delete(`${SERVER_URL}/shared_devices/${deleteRow.id}`);
      setShares((prev) => prev.filter((s) => s.id !== deleteRow.id));

      const preostalo = filteredRows.length - 1;
      if (page > 0 && page * rowsPerPage >= preostalo) setPage(page - 1);

      setDeleteRow(null);
    } catch (err) {
      console.log('Greška pri brisanju dijeljenja', err);
      setDeleteError('Prekid dijeljenja nije uspio. Provjerite vezu sa serverom i pokušajte ponovo.');
    } finally {
      setDeleting(false);
    }
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');

    // Nova kolona = nova vrsta pretrage, pa stari tekst briše
    if (property !== orderBy) {
      setFilterName('');
      setPage(0);
    }
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const handleFilterByName = (event) => {
    setPage(0);
    setFilterName(event.target.value);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - filteredRows.length) : 0;

  const isNotFound = !filteredRows.length && !!filterName;

  const today = new Date();
  const datum = `Datum: ${today.getDate()}.${today.getMonth() + 1}.${today.getFullYear()}. god.`;

  const ukupnoKolona = TABLE_HEAD.length;

  // --- Render ---

  return (
    <>
      <Helmet>
        <title> GPS Dogs Tracking System </title>
      </Helmet>

      <Dialog open={Boolean(deleteRow)} onClose={handleCloseDelete} maxWidth="xs" fullWidth>
        <DialogTitle>Prekid dijeljenja</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {deleteRow && (
              <>
                Da li želite prestati dijeliti uređaj <strong>{deleteRow.imepsa}</strong> (vlasnik{' '}
                {deleteRow.vlasnik}) sa korisnikom <strong>{deleteRow.posudjeno}</strong>? Sam uređaj se ne briše.
              </>
            )}
          </DialogContentText>
          {deleteError && (
            <Typography variant="body2" sx={{ color: 'error.main', mt: 2 }}>
              {deleteError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDelete} disabled={deleting}>
            Ne
          </Button>
          <Button color="error" onClick={handleConfirmDelete} disabled={deleting} autoFocus>
            {deleting ? 'Uklanjam...' : 'Da'}
          </Button>
        </DialogActions>
      </Dialog>

      <Container maxWidth={false}>
        <Typography variant="subtitle2" noWrap>
          Pregled dijeljenih uređaja
        </Typography>

        <Card>
          <UserListToolbar
            numSelected={0}
            filterName={filterName}
            onFilterName={handleFilterByName}
            placeholder={getSearchPlaceholder(orderBy)}
          />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <UserListHead order={order} orderBy={orderBy} headLabel={TABLE_HEAD} onRequestSort={handleRequestSort} />
                <TableBody>
                  {filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                    const { id, deviceId, tip, tiptrackera, vlasnik, imepsa, IMEI, posudjeno } = row;

                    return (
                      <TableRow hover key={id} tabIndex={-1}>
                        <TableCell component="th" scope="row">
                          <Typography variant="subtitle2" noWrap>
                            {deviceId}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={2}>
                            <Avatar
                              alt={imepsa}
                              src={tip ? `/assets/images/lovackipsi/icon_${tip}.png` : undefined}
                              sx={{ width: 50, height: 50 }}
                              variant="rounded"
                            >
                              <Iconify icon="mdi:paw" width={28} />
                            </Avatar>
                            <Avatar
                              alt={tiptrackera}
                              src={
                                tiptrackera
                                  ? `/assets/images/trackers/icon_${tiptrackera.toLowerCase()}.png`
                                  : undefined
                              }
                              sx={{ width: 30, height: 30 }}
                              variant="rounded"
                            >
                              <Iconify icon="mdi:crosshairs-gps" width={18} />
                            </Avatar>
                          </Stack>
                        </TableCell>

                        <TableCell align="left">
                          <Typography variant="subtitle2" noWrap>
                            {vlasnik}
                          </Typography>
                        </TableCell>

                        <TableCell align="left">
                          <Typography variant="subtitle2" noWrap>
                            {imepsa}
                          </Typography>
                        </TableCell>

                        <TableCell align="left">
                          <Typography variant="subtitle2" noWrap>
                            {IMEI}
                          </Typography>
                        </TableCell>

                        <TableCell align="left">
                          <Typography variant="subtitle2" noWrap>
                            {tiptrackera}
                          </Typography>
                        </TableCell>

                        <TableCell align="left">
                          <Typography variant="subtitle2" noWrap>
                            {posudjeno}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Link to="/SharedDevices/update">
                            <Button onClick={() => setData(row)}>Update</Button>
                          </Link>
                        </TableCell>

                        <TableCell>
                          <Button color="error" onClick={() => handleClickDelete(row)}>
                            Delete
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={ukupnoKolona} />
                    </TableRow>
                  )}
                </TableBody>

                {isNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={ukupnoKolona} sx={{ py: 3 }}>
                        <Paper sx={{ textAlign: 'center' }}>
                          <Typography variant="h6" paragraph>
                            Nema rezultata
                          </Typography>
                          <Typography variant="body2">
                            Ništa nije pronađeno za &nbsp;
                            <strong>&quot;{filterName}&quot;</strong> u koloni {getColumn(orderBy)?.label}.
                          </Typography>
                        </Paper>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredRows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>

        <br />

        <Button onClick={() => setLoading(!loading)}>
          {loading ? 'Zatvori izvještaj' : 'Generate Report'}
        </Button>

        {loading && (
          <div className="child">
            <PDFViewer className="child">
              <SharedDevicesReport rows={filteredRows} datum={datum} />
            </PDFViewer>
          </div>
        )}
      </Container>
    </>
  );
}